import { useEffect, useRef, useState } from 'react';
import { Keyboard, Platform, useWindowDimensions } from 'react-native';

/**
 * How much of the screen the keyboard is covering, in points. `0` when down.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS INSTEAD OF `KeyboardAvoidingView`
 * ---------------------------------------------------------------------------
 * `KeyboardAvoidingView` with `behavior="height"` - the documented Android
 * strategy - works by letting the WINDOW shrink and then filling what is left.
 * That depends on `android:windowSoftInputMode="adjustResize"` actually
 * resizing the window, and from Android 15 it does not: apps draw edge to edge
 * by default, the window keeps its full height, and the keyboard arrives as an
 * inset instead. The manifest still says `adjustResize` and the system now
 * ignores it.
 *
 * The visible result was a bottom-anchored panel - the address form, every
 * sheet with a field in it - sitting underneath the keys with its input and
 * its submit button unreachable. Nothing was wrong with the layout; the view
 * it was avoiding with never shrank.
 *
 * So the height is read straight off the keyboard events and applied by the
 * caller, which works the same on both platforms and does not care whether the
 * window resized.
 *
 * ---------------------------------------------------------------------------
 * WHICH EVENTS, AND WHY THEY DIFFER
 * ---------------------------------------------------------------------------
 * iOS emits `keyboardWillShow` a frame BEFORE the keyboard animates, so the
 * layout can move with it and the two travel together. Android has no "will"
 * event worth using - `keyboardDidShow` is the first point the height is
 * known - so it lands after the fact. That is why an iOS transition looks
 * smooth and an Android one snaps; it is the platform, not the caller.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE CALLER GETS IS THE *UNCOMPENSATED* OVERLAP, NOT THE RAW HEIGHT
 * ---------------------------------------------------------------------------
 * Android before 15 still honours `adjustResize`: the WINDOW shrinks by the
 * keyboard's height, so a bottom-anchored panel has already been carried up
 * before the caller adds any padding of its own. Handing back the raw event
 * height there meant the keyboard was compensated for TWICE - window resize
 * plus padding - and the sheet was thrown a full keyboard-height above the
 * keys, which on a Samsung with a tall keyboard parked the OTP form at the
 * top of the screen.
 *
 * So the hook watches the window's own height as well: whatever the window
 * has already shrunk by is subtracted from the keyboard's height, and only
 * the remainder - the part actually covering the app - is returned.
 *
 *   Android 15+ / iOS   window never shrinks   → remainder = full height
 *   Android ≤14         window shrinks fully   → remainder = 0
 *   split/floating keyboards, partial resizes  → whatever is left over
 *
 * One number, correct on every device, and no caller has to know which world
 * it is running in.
 */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);
  const { height: windowHeight } = useWindowDimensions();

  /*
   * The window's height as it was with no keyboard up - the yardstick the
   * shrink is measured against. Only ever updated while the keyboard is
   * down, so mid-animation re-renders cannot move the baseline under the
   * subtraction below.
   */
  const baseline = useRef(windowHeight);
  useEffect(() => {
    if (height === 0) {
      baseline.current = windowHeight;
    }
  }, [height, windowHeight]);

  useEffect(() => {
    const ios = Platform.OS === 'ios';

    const show = Keyboard.addListener(
      ios ? 'keyboardWillShow' : 'keyboardDidShow',
      event => setHeight(event.endCoordinates.height),
    );
    const hide = Keyboard.addListener(
      ios ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setHeight(0),
    );

    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  if (height === 0) {
    return 0;
  }

  /* How much of the keyboard the window has already made room for. */
  const resizedAway = Math.max(0, baseline.current - windowHeight);
  return Math.max(0, height - resizedAway);
}
